/**
 * Centralized Logging Service
 * 
 * Provides structured logging with different log levels.
 * Debug logs are disabled in production for security and performance.
 * 
 * Usage:
 * - logger.debug('Debug info', { data }) - Only shows in development
 * - logger.info('Information', { data }) - Only shows in development
 * - logger.warn('Warning', { data }) - Shows in all environments
 * - logger.error('Error message', error, { data }) - Shows in all environments
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  isDevelopment: boolean;
  enableDebugLogs: boolean;
  enableInfoLogs: boolean;
}

class Logger {
  private config: LoggerConfig;

  constructor() {
    this.config = {
      isDevelopment: import.meta.env.DEV,
      enableDebugLogs: import.meta.env.DEV, // Only in development
      enableInfoLogs: import.meta.env.DEV, // Only in development
    };
  }

  /**
   * Debug level logging - for detailed troubleshooting
   * Only logs in development mode
   */
  debug(message: string, ...args: any[]): void {
    if (this.config.enableDebugLogs) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Info level logging - for general informational messages
   * Only logs in development mode
   */
  info(message: string, ...args: any[]): void {
    if (this.config.enableInfoLogs) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  /**
   * Warning level logging - for potentially harmful situations
   * Logs in all environments
   */
  warn(message: string, ...args: any[]): void {
    console.warn(`[WARN] ${message}`, ...args);
    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
  }

  /**
   * Error level logging - for error conditions
   * Logs in all environments and should be sent to error tracking
   */
  error(message: string, error?: Error | unknown, ...args: any[]): void {
    if (error instanceof Error) {
      console.error(`[ERROR] ${message}`, error, ...args);
    } else {
      console.error(`[ERROR] ${message}`, error, ...args);
    }
    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    // Example: Sentry.captureException(error, { extra: { message, ...args } });
  }

  /**
   * Log API requests (debug level)
   */
  api(method: string, url: string, details?: Record<string, any>): void {
    this.debug(`API ${method} ${url}`, details);
  }

  /**
   * Log authentication events (info level)
   */
  auth(message: string, details?: Record<string, any>): void {
    this.info(`[AUTH] ${message}`, details);
  }

  /**
   * Log navigation events (debug level)
   */
  navigation(message: string, details?: Record<string, any>): void {
    this.debug(`[NAV] ${message}`, details);
  }

  /**
   * Group related logs together (development only)
   */
  group(label: string, fn: () => void): void {
    if (this.config.isDevelopment) {
      console.group(label);
      fn();
      console.groupEnd();
    }
  }

  /**
   * Log execution time of a function (development only)
   */
  time(label: string): void {
    if (this.config.isDevelopment) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.config.isDevelopment) {
      console.timeEnd(label);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for extensibility
export type { LogLevel, LoggerConfig };
